import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { CURRENCY_DISPLAY_ORDER } from "@/helpers/currencyPresentation"
import { useAmountHint } from "@/hooks/useAmountHint"
import { useCatalogs } from "@/hooks/useCatalogs"
import {
  compactSuffixesOf,
  deriveCounters,
  toCounterViews,
  trackCounters
} from "@/hooks/useWalletCounters/helpers"
import {
  ICounterRecord,
  IWalletCountersView
} from "@/hooks/useWalletCounters/types"
import { IWalletBalance, useGetWalletQuery } from "@/store/apis/walletAPI"

const EMPTY_BALANCES: IWalletBalance[] = []
const NO_RECORDS: ReadonlyMap<string, ICounterRecord> = new Map()

export const useWalletCounters = (): IWalletCountersView => {
  const { balances, loaded } = useGetWalletQuery(undefined, {
    selectFromResult: ({ data }) => ({
      balances: data?.balances ?? EMPTY_BALANCES,
      loaded: data !== undefined
    })
  })
  const { currencies } = useCatalogs()
  const { t, i18n } = useTranslation("catalog")
  const locale = i18n.language
  const suffixes = useMemo(() => compactSuffixesOf(t), [t, locale])
  const hint = useAmountHint()

  const recordsRef = useRef(NO_RECORDS)
  const inputs = useMemo(
    () =>
      deriveCounters(
        loaded ? balances : null,
        currencies,
        locale,
        suffixes,
        CURRENCY_DISPLAY_ORDER
      ),
    [balances, loaded, currencies, locale, suffixes]
  )
  const records = useMemo(
    () => trackCounters(recordsRef.current, inputs, loaded),
    [inputs, loaded]
  )
  useEffect(() => {
    recordsRef.current = records
  }, [records])

  const counters = useMemo(
    () => toCounterViews(inputs, records, hint.openCode),
    [inputs, records, hint.openCode]
  )

  return useMemo(
    () => ({ counters, onToggleHint: hint.toggle }),
    [counters, hint.toggle]
  )
}
